import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';

const useStyles = makeStyles(theme => ({
    card: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 2.25),
        marginBottom: theme.spacing(2),
        border: '1px dashed var(--dm-border, #c9d3de)',
        borderRadius: 10,
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    icon: {
        width: 36,
        height: 36,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        color: theme.palette.primary.main,
        backgroundColor: 'rgba(61, 125, 221, 0.12)',
    },
    body: {
        flex: '1 1 320px',
        minWidth: 0,
    },
    title: {
        color: 'var(--dm-text-strong, #1f2f4a)',
        fontSize: '0.9375rem',
        fontWeight: 700,
    },
    text: {
        marginTop: 2,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
    },
    columns: {
        marginTop: 4,
        color: 'var(--dm-muted, #8a96a5)',
        fontSize: '0.6875rem',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.25),
        flexShrink: 0,
    },
    actionsColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.5),
        flexShrink: 0,
    },
    button: {
        minWidth: 170,
        textTransform: 'none',
        fontWeight: 700,
    },
    csvLink: {
        padding: theme.spacing(0.25, 0.75),
        minWidth: 0,
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    fileInput: {
        display: 'none',
    },
    hint: {
        flexBasis: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderRadius: 8,
        color: '#8a5a00',
        backgroundColor: 'rgba(245, 158, 11, 0.12)',
        fontSize: '0.8125rem',
    },
}));

/** Download only needs users; upload needs licences too. */
export const bulkActionState = ({ busy, userCount, licenceCount }) => ({
    canDownload: !busy && userCount > 0,
    canUpload: !busy && userCount > 0 && licenceCount > 0,
});

const BulkAssignCard = ({
    onDownload,
    onFileSelected,
    busy,
    downloadDisabled,
    uploadDisabled,
    userCount = 0,
    licenceCount = 0,
    onGoToAllocation,
    onGoToUsers,
}) => {
    const classes = useStyles();
    const inputRef = useRef(null);

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (file) onFileSelected(file);
    };

    return (
        <section className={classes.card} aria-labelledby="bulk-assign-title">
            <span className={classes.icon} aria-hidden="true"><PublishOutlinedIcon fontSize="small" /></span>
            <div className={classes.body}>
                <Typography id="bulk-assign-title" className={classes.title}>Bulk assign from a file</Typography>
                <Typography className={classes.text}>
                    Assign licences to many users at once. Users are matched on work email; rows that exceed the project allocation are flagged for review.
                </Typography>
                <Typography className={classes.columns}>
                    Template columns: Work email, then Y or N per licence. Leave a cell blank to keep it unchanged.
                    {userCount > 0 ? ` Includes all ${userCount} learners in this project.` : ''}
                </Typography>
            </div>
            <div className={classes.actionsColumn}>
                <div className={classes.actions}>
                    <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        className={classes.button}
                        onClick={() => onDownload('xlsx')}
                        disabled={downloadDisabled || Boolean(busy)}
                    >
                        {busy === 'download' ? <CircularProgress size={18} color="inherit" /> : 'Download template'}
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => inputRef.current && inputRef.current.click()}
                        disabled={uploadDisabled || Boolean(busy)}
                    >
                        {busy === 'upload' ? <CircularProgress size={18} color="inherit" /> : 'Upload CSV or Excel'}
                    </Button>
                    <input
                        ref={inputRef}
                        className={classes.fileInput}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                    />
                </div>
                <Button
                    type="button"
                    size="small"
                    color="primary"
                    className={classes.csvLink}
                    onClick={() => onDownload('csv')}
                    disabled={downloadDisabled || Boolean(busy)}
                >
                    {busy === 'download-csv' ? <CircularProgress size={14} color="inherit" /> : 'Download as CSV'}
                </Button>
            </div>
            {userCount === 0 ? (
                <div className={classes.hint} role="note">
                    <span>No users have been added to this project yet, so the template, upload and Quick assign are off.</span>
                    {onGoToUsers ? (
                        <Button size="small" variant="outlined" color="primary" onClick={onGoToUsers}>Go to Users</Button>
                    ) : null}
                </div>
            ) : null}
            {userCount > 0 && licenceCount === 0 ? (
                <div className={classes.hint} role="note">
                    <span>No licences are allocated to this project yet, so uploads and Quick assign are off. The template only lists users until licences are allocated.</span>
                    {onGoToAllocation ? (
                        <Button size="small" variant="outlined" color="primary" onClick={onGoToAllocation}>Go to Allocation</Button>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
};

export default BulkAssignCard;
